import React, { useState, useRef, useEffect } from 'react';
import { ScheduleView } from './components/ScheduleView';
import { StandingsView } from './components/StandingsView';
import { ClubScheduleView } from './components/ClubScheduleView';
import { NotificationDialog } from './components/NotificationDialog';
import { GameDetail } from './components/GameDetail';
import { usePreloadTeamData } from './hooks/usePreloadTeamData';
import { Team, TEAMS, Game } from './types';

function App() {
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
  const [selectedView, setSelectedView] = useState<'club' | Team['id']>('club'); // Default zum Vereinsspielplan
  const [teamTabView, setTeamTabView] = useState<'upcoming' | 'results' | 'standings'>('upcoming');
  const [clubTabView, setClubTabView] = useState<'upcoming' | 'results'>('results');
  const [leagueName, setLeagueName] = useState<string>('');
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const navRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [carouselHeight, setCarouselHeight] = useState<number | null>(null);
  const [clubCarouselHeight, setClubCarouselHeight] = useState<number | null>(null);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [savedScrollPosition, setSavedScrollPosition] = useState<number>(0);
  const [showNotificationDialog, setShowNotificationDialog] = useState(false);
  const [notificationContext, setNotificationContext] = useState<{ teamId?: string; teamName: string; isClub: boolean }>({ teamName: '', isClub: false });

  const [dragOffset, setDragOffset] = useState(0);

  // Preload all team data for smooth navigation
  usePreloadTeamData();

  // Wrapper function to handle game selection with scroll position
  const handleGameSelect = (game: Game | null) => {
    if (game) {
      // Save current scroll position before navigating to detail
      setSavedScrollPosition(window.scrollY);
    } else {
      // Restore scroll position when going back
      setTimeout(() => {
        window.scrollTo(0, savedScrollPosition);
      }, 0);
    }
    setSelectedGame(game);
  };

  // Carousel logic for team navigation
  type ClubView = {
    id: string;
    type: 'club';
    tabs: string[];
  };

  type TeamView = {
    id: string;
    type: 'team';
    team: Team;
    tabs: string[];
  };

  type ViewItem = ClubView | TeamView;

  const getAllViews = (): ViewItem[] => {
    const views: ViewItem[] = [];

    // Club view (always first)
    views.push({
      id: 'club',
      type: 'club' as const,
      tabs: ['upcoming', 'results']
    });

    // Team views
    TEAMS.forEach(team => {
      views.push({
        id: team.id,
        type: 'team' as const,
        team: team,
        tabs: ['upcoming', 'results', 'standings']
      });
    });

    return views;
  };

  const allViews = getAllViews();
  const currentViewIndex = selectedView === 'club'
    ? 0
    : allViews.findIndex(view => view.id === selectedView);
  const currentTabIndex = selectedView === 'club'
    ? (clubTabView === 'upcoming' ? 0 : 1)
    : (teamTabView === 'upcoming' ? 0 : teamTabView === 'results' ? 1 : 2);

  // Calculate initial position for carousel (in percentage)
  const getCarouselPosition = () => {
    if (selectedView === 'club') {
      // Club carousel: 0% for 'upcoming', -50% for 'finished' (only 2 tabs, each 50% wide)
      return clubTabView === 'upcoming' ? 0 : -50;
    } else {
      // Team carousel: calculate based on absolute position
      let absoluteIndex = 0;

      for (let i = 0; i < currentViewIndex; i++) {
        absoluteIndex += allViews[i].tabs.length;
      }
      absoluteIndex += currentTabIndex;

      const totalTabs = allViews.reduce((total, view) => total + view.tabs.length, 0);
      return -(absoluteIndex * (100 / totalTabs));
    }
  };

  // Update dragOffset when view/tab changes
  useEffect(() => {
    const newPosition = getCarouselPosition();
    setDragOffset(newPosition);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedView, teamTabView, clubTabView]);

  // Dynamically adjust carousel height to current slide
  useEffect(() => {
    if (selectedView !== 'club') {
      const adjustCarouselHeight = () => {
        if (contentRef.current) {
          const slides = contentRef.current.children;
          if (slides.length > 0) {
            // Calculate absolute index based on current view and tab
            let absoluteIndex = 0;
            for (let i = 0; i < currentViewIndex; i++) {
              absoluteIndex += allViews[i].tabs.length;
            }
            absoluteIndex += currentTabIndex;

            const currentSlide = slides[absoluteIndex] as HTMLElement;
            if (currentSlide) {
              const height = currentSlide.offsetHeight;
              setCarouselHeight(height);
            }
          }
        }
      };

      // Adjust height after layout changes
      const timer = setTimeout(adjustCarouselHeight, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedView, teamTabView, clubTabView, currentTabIndex, currentViewIndex, allViews]);

  // Separate height management for club carousel
  useEffect(() => {
    if (selectedView === 'club') {
      // For club carousel, we need to measure the height of the current view content
      const adjustHeight = () => {
        // Find all ClubScheduleView containers and measure the active one
        const clubContainers = document.querySelectorAll('[data-club-view]');
        if (clubContainers.length > 0) {
          const activeIndex = clubTabView === 'upcoming' ? 0 : 1;
          const activeContainer = clubContainers[activeIndex] as HTMLElement;
          if (activeContainer) {
            setClubCarouselHeight(activeContainer.offsetHeight);
          }
        }
      };

      // Initial measurement
      const timer = setTimeout(adjustHeight, 100);

      // Re-measure when window resizes or content changes
      window.addEventListener('resize', adjustHeight);

      // Use MutationObserver to detect content changes
      const observer = new MutationObserver(adjustHeight);
      const clubContainers = document.querySelectorAll('[data-club-view]');
      clubContainers.forEach(container => {
        observer.observe(container, { childList: true, subtree: true });
      });

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', adjustHeight);
        observer.disconnect();
      };
    }
  }, [selectedView, clubTabView]);

  const checkScrollArrows = () => {
    if (navRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = navRef.current;
      setShowLeftArrow(scrollLeft > 0);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkScrollArrows();
    const nav = navRef.current;
    if (nav) {
      nav.addEventListener('scroll', checkScrollArrows);
      return () => nav.removeEventListener('scroll', checkScrollArrows);
    }
  }, []);

  useEffect(() => {
    checkScrollArrows();
  }, [selectedView]);

  // Scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 200);
      setIsHeaderScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="bg-gray-50">
      <header className={`bg-gradient-to-r from-fortuna-600 to-fortuna-700 text-white shadow-xl sticky top-0 z-30 transition-all duration-300 ${
        isHeaderScrolled ? 'py-2' : 'py-6'
      }`}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center space-x-4">
            <div className={`bg-white/10 backdrop-blur-sm rounded-2xl flex items-center justify-center ring-2 ring-white/20 shadow-lg transition-all duration-300 ${
              isHeaderScrolled ? 'w-10 h-10' : 'w-14 h-14'
            }`}>
              <img
                src="/images/SC_Fortuna_Koeln_Logo.png"
                alt="SC Fortuna Köln Logo"
                className={`object-contain transition-all duration-300 ${
                  isHeaderScrolled ? 'w-7 h-7' : 'w-11 h-11'
                }`}
                onError={(e) => {
                  // Fallback to text logo if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  target.parentElement!.innerHTML = '<span class="text-white font-bold text-2xl">FK</span>';
                }}
              />
            </div>
            <div>
              <h1 className={`font-bold text-white tracking-wide transition-all duration-300 ${
                isHeaderScrolled ? 'text-xl' : 'text-3xl'
              }`} style={{ fontFamily: 'Oswald, sans-serif' }}>SC FORTUNA KÖLN</h1>
              <p className={`text-white/80 font-medium transition-all duration-300 ${
                isHeaderScrolled ? 'text-xs mt-0' : 'text-sm mt-1'
              }`}>Handball</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-4">
        <div className="bg-white rounded-lg shadow">
          {/* Haupt-Navigation (Teams + Verein) */}
<div className={`border-b border-gray-200 relative sticky bg-white z-20 transition-all duration-300 ${
              isHeaderScrolled ? 'top-[60px]' : 'top-[94px]'
            }`}>
            <nav ref={navRef} className="flex space-x-0 px-4 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => {
                  setSelectedView('club');
                  setSelectedTeam(null);
                  handleGameSelect(null); // Reset selected game when switching views
                  // Synchronisiere Tab-Auswahl: Tabelle → Ergebnisse, Spielplan/Ergebnisse bleiben
                  if (teamTabView === 'standings') {
                    setClubTabView('results');
                  } else if (teamTabView === 'upcoming') {
                    setClubTabView('upcoming');
                  } else {
                    setClubTabView('results');
                  }
                }}
                className={`py-3 px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  selectedView === 'club'
                    ? 'border-fortuna-500 text-fortuna-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Verein
              </button>
              {TEAMS.map((team) => (
                <button
                  key={team.id}
                  onClick={() => {
                    setSelectedView(team.id);
                    setSelectedTeam(team);
                    handleGameSelect(null); // Reset selected game when switching views
                    // Synchronisiere Tab-Auswahl von Verein zu Team
                    if (clubTabView === 'upcoming') {
                      setTeamTabView('upcoming');
                    } else if (clubTabView === 'results') {
                      setTeamTabView('results');
                    }
                    setLeagueName(''); // Reset Liga-Name für neues Team
                  }}
                  className={`py-3 px-3 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                    selectedView === team.id
                      ? 'border-fortuna-500 text-fortuna-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {team.name}
                </button>
              ))}
            </nav>

            {/* Left arrow */}
            {showLeftArrow && (
              <div className="absolute left-0 -top-px h-full w-8 bg-gradient-to-r from-white to-transparent flex items-center justify-start pl-2 pointer-events-none">
                <span className="text-gray-500 text-sm font-medium py-3">&lt;</span>
              </div>
            )}

            {/* Right arrow */}
            {showRightArrow && (
              <div className="absolute right-0 -top-px h-full w-8 bg-gradient-to-l from-white to-transparent flex items-center justify-end pr-2 pointer-events-none">
                <span className="text-gray-500 text-sm font-medium py-3">&gt;</span>
              </div>
            )}
          </div>

          {/* Content für Teams */}
          {selectedTeam && selectedView !== 'club' && (
            <>
              {/* Tab Navigation - Mobile: 3 Tabs, Desktop: 2 Tabs */}
              <div className={`border-b border-gray-100 sticky bg-white z-10 -mt-px transition-all duration-300 ${
                isHeaderScrolled ? 'top-[107px]' : 'top-11'
              }`}>
                <nav className="flex items-center justify-between px-6">
                  <div className="flex space-x-6">
                    <button
                      onClick={() => setTeamTabView('upcoming')}
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                        teamTabView === 'upcoming'
                          ? 'border-fortuna-400 text-fortuna-600'
                          : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200'
                      }`}
                    >
                      Spielplan
                    </button>
                    <button
                      onClick={() => setTeamTabView('results')}
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                        teamTabView === 'results'
                          ? 'border-fortuna-400 text-fortuna-600'
                          : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200'
                      }`}
                    >
                      Ergebnisse
                    </button>
                    {/* Tabelle Tab nur auf Mobile */}
                    <button
                      onClick={() => setTeamTabView('standings')}
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors lg:hidden ${
                        teamTabView === 'standings'
                          ? 'border-fortuna-400 text-fortuna-600'
                          : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200'
                      }`}
                    >
                      Tabelle
                    </button>
                  </div>

                  {/* Notification Bell for Team */}
                  <button
                    onClick={() => {
                      setNotificationContext({
                        teamId: selectedTeam?.id,
                        teamName: selectedTeam?.name || '',
                        isClub: false
                      });
                      setShowNotificationDialog(true);
                    }}
                    className={`p-2 rounded-full transition-colors ${
                      (() => {
                        const stored = localStorage.getItem(`notifications_${selectedTeam?.id}`);
                        if (!stored) return 'text-gray-400 hover:bg-gray-100 hover:text-gray-600';
                        try {
                          const data = JSON.parse(stored);
                          return data.enabled ? 'bg-fortuna-100 text-fortuna-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600';
                        } catch {
                          return stored === 'enabled' ? 'bg-fortuna-100 text-fortuna-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600';
                        }
                      })()
                    }`}
                    title="Benachrichtigungen verwalten"
                  >
                    <svg className="w-5 h-5" fill={(() => {
                      const stored = localStorage.getItem(`notifications_${selectedTeam?.id}`);
                      if (!stored) return 'none';
                      try {
                        const data = JSON.parse(stored);
                        return data.enabled ? 'currentColor' : 'none';
                      } catch {
                        return stored === 'enabled' ? 'currentColor' : 'none';
                      }
                    })()} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </button>
                </nav>
              </div>

              {/* Liga-Header (nur Desktop, über ganze Breite) */}
              {leagueName && !selectedGame && (
                <div className="hidden lg:block px-6 pt-4 pb-4">
                  <h1 className="text-l font-bold text-gray-900">{leagueName}</h1>
                </div>
              )}

              {/* Liga-Header (nur Mobile) */}
              {leagueName && !selectedGame && (
                <div className="lg:hidden px-4 pt-3">
                  <h1 className="text-l font-bold text-gray-900">{leagueName}</h1>
                </div>
              )}

              {/* Desktop: Side-by-side Layout oder Game Detail */}
              {selectedGame ? (
                // Game Detail - Full Width auf Desktop
                <div className="hidden lg:block">
                  <GameDetail
                    game={selectedGame}
                    leagueName={leagueName}
                    onBack={() => handleGameSelect(null)}
                  />
                </div>
              ) : (
                // Normal Side-by-Side Layout
                <div className="hidden lg:grid lg:grid-cols-2 lg:gap-6 px-6 pb-6">
                  <div>
                    {/* Schedule Content */}
                    <ScheduleView
                      team={selectedTeam}
                      onLeagueNameLoad={setLeagueName}
                      filter={teamTabView === 'upcoming' ? 'upcoming' : teamTabView === 'results' ? 'finished' : undefined}
                      onGameSelect={handleGameSelect}
                    />
                  </div>
                  <div>
                    {/* Sticky Tabelle - top Position berücksichtigt Header + Navigation + Tabs */}
                    <div className={`sticky transition-all duration-300 ${
                      isHeaderScrolled ? 'top-[150px]' : 'top-[210px]'
                    }`}>
                      <StandingsView team={selectedTeam} />
                    </div>
                  </div>
                </div>
              )}

            {/* Mobile: Carousel Content or Detail View */}
            <div className="lg:hidden">
              {selectedGame ? (
                // Game Detail on Mobile
                <GameDetail
                  game={selectedGame}
                  leagueName={leagueName}
                  onBack={() => handleGameSelect(null)}
                />
              ) : (
                // Normal Carousel
                <div
                  className="overflow-hidden transition-all duration-300"
                  style={{ height: carouselHeight ? `${carouselHeight}px` : 'auto' }}
                >
                  <div
                    ref={contentRef}
                    className="flex items-start"
                    style={{
                      transform: `translateX(${dragOffset}%)`,
                      transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                      width: `${allViews.reduce((total, view) => total + view.tabs.length, 0) * 100}%`
                    }}
                  >
                    {allViews.map((view) =>
                      view.tabs.map((tab, tabIndex) => (
                        <div key={`${view.id}-${tab}`} className="flex-shrink-0 p-4" style={{width: `${100 / allViews.reduce((total, view) => total + view.tabs.length, 0)}%`}}>
                            {view.type === 'club' ? (
                              <ClubScheduleView filter={tab as 'upcoming' | 'finished'} />
                            ) : (
                              tab === 'upcoming' ? (
                                <ScheduleView
                                  team={(view as TeamView).team}
                                  onLeagueNameLoad={view.id === selectedView ? setLeagueName : undefined}
                                  filter="upcoming"
                                  onGameSelect={handleGameSelect}
                                />
                              ) : tab === 'results' ? (
                                <ScheduleView
                                  team={(view as TeamView).team}
                                  onLeagueNameLoad={view.id === selectedView ? setLeagueName : undefined}
                                  filter="finished"
                                  onGameSelect={handleGameSelect}
                                />
                              ) : (
                                <StandingsView team={(view as TeamView).team} />
                              )
                            )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
            </>
          )}

          {/* Content für Vereinsspielplan */}
          {selectedView === 'club' && (
            <>
              {/* Desktop: Tab Navigation für Verein */}
              <div className={`hidden lg:block border-b border-gray-100 px-6 sticky bg-white z-10 -mt-px transition-all duration-300 ${
                isHeaderScrolled ? 'top-[107px]' : 'top-[141px]'
              }`}>
                <nav className="flex items-center justify-between">
                  <div className="flex space-x-8">
                    <button
                      onClick={() => setClubTabView('upcoming')}
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                        clubTabView === 'upcoming'
                          ? 'border-fortuna-400 text-fortuna-600'
                          : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200'
                      }`}
                    >
                      Spielplan
                    </button>
                    <button
                      onClick={() => setClubTabView('results')}
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                        clubTabView === 'results'
                          ? 'border-fortuna-400 text-fortuna-600'
                          : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200'
                      }`}
                    >
                      Ergebnisse
                    </button>
                  </div>

                  {/* Notification Bell for Club */}
                  <button
                    onClick={() => {
                      setNotificationContext({
                        teamName: 'Vereins-Nachrichten',
                        isClub: true
                      });
                      setShowNotificationDialog(true);
                    }}
                    className={`p-2 rounded-full transition-colors ${
                      (() => {
                        const stored = localStorage.getItem('notifications_club');
                        if (!stored) return 'text-gray-400 hover:bg-gray-100 hover:text-gray-600';
                        try {
                          const data = JSON.parse(stored);
                          return data.enabled ? 'bg-fortuna-100 text-fortuna-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600';
                        } catch {
                          return stored === 'enabled' ? 'bg-fortuna-100 text-fortuna-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600';
                        }
                      })()
                    }`}
                    title="Benachrichtigungen verwalten"
                  >
                    <svg className="w-5 h-5" fill={(() => {
                      const stored = localStorage.getItem('notifications_club');
                      if (!stored) return 'none';
                      try {
                        const data = JSON.parse(stored);
                        return data.enabled ? 'currentColor' : 'none';
                      } catch {
                        return stored === 'enabled' ? 'currentColor' : 'none';
                      }
                    })()} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </button>
                </nav>
              </div>

              {/* Mobile: Tab Navigation für Verein */}
              <div className={`border-b border-gray-100 lg:hidden sticky bg-white z-10 -mt-px transition-all duration-300 ${
                isHeaderScrolled ? 'top-[107px]' : 'top-11'
              }`}>
                <nav className="flex items-center justify-between px-6">
                  <div className="flex space-x-8">
                    <button
                      onClick={() => setClubTabView('upcoming')}
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                        clubTabView === 'upcoming'
                          ? 'border-fortuna-400 text-fortuna-600'
                          : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200'
                      }`}
                    >
                      Spielplan
                    </button>
                    <button
                      onClick={() => setClubTabView('results')}
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                        clubTabView === 'results'
                          ? 'border-fortuna-400 text-fortuna-600'
                          : 'border-transparent text-gray-400 hover:text-gray-600 hover:border-gray-200'
                      }`}
                    >
                      Ergebnisse
                    </button>
                  </div>

                  {/* Notification Bell for Club */}
                  <button
                    onClick={() => {
                      setNotificationContext({
                        teamName: 'Vereins-Nachrichten',
                        isClub: true
                      });
                      setShowNotificationDialog(true);
                    }}
                    className={`p-2 rounded-full transition-colors ${
                      (() => {
                        const stored = localStorage.getItem('notifications_club');
                        if (!stored) return 'text-gray-400 hover:bg-gray-100 hover:text-gray-600';
                        try {
                          const data = JSON.parse(stored);
                          return data.enabled ? 'bg-fortuna-100 text-fortuna-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600';
                        } catch {
                          return stored === 'enabled' ? 'bg-fortuna-100 text-fortuna-600' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600';
                        }
                      })()
                    }`}
                    title="Benachrichtigungen verwalten"
                  >
                    <svg className="w-5 h-5" fill={(() => {
                      const stored = localStorage.getItem('notifications_club');
                      if (!stored) return 'none';
                      try {
                        const data = JSON.parse(stored);
                        return data.enabled ? 'currentColor' : 'none';
                      } catch {
                        return stored === 'enabled' ? 'currentColor' : 'none';
                      }
                    })()} stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                  </button>
                </nav>
              </div>

              {/* Verein Header */}
              {!selectedGame && (
                <div className="lg:hidden px-4 pt-3">
                  <h1 className="text-l font-bold text-gray-900">Verein</h1>
                </div>
              )}

              {/* Desktop: Verein Header */}
              {!selectedGame && (
                <div className="hidden lg:block px-6 pt-4 pb-4">
                  <h1 className="text-l font-bold text-gray-900">Verein</h1>
                </div>
              )}

              {/* Desktop: Verein Content */}
              {selectedGame ? (
                // Game Detail - Full Width auf Desktop
                <div className="hidden lg:block px-6 pb-6">
                  <GameDetail
                    game={selectedGame}
                    leagueName={(selectedGame as any).leagueName}
                    onBack={() => handleGameSelect(null)}
                  />
                </div>
              ) : (
                // Normal Schedule View
                <div className="hidden lg:block px-6 pb-6">
                  <ClubScheduleView filter={clubTabView === 'results' ? 'finished' : 'upcoming'} onGameSelect={handleGameSelect} />
                </div>
              )}

              {/* Vereinsspielplan Mobile Carousel */}
              <div className="lg:hidden">
                {selectedGame ? (
                  // Game Detail on Mobile
                  <GameDetail
                    game={selectedGame}
                    leagueName={(selectedGame as any).leagueName}
                    onBack={() => handleGameSelect(null)}
                  />
                ) : (
                  // Normal Carousel
                  <div
                    className="overflow-hidden transition-all duration-300"
                    style={{ height: clubCarouselHeight ? `${clubCarouselHeight}px` : 'auto' }}
                  >
                    <div
                      className="flex items-start"
                      style={{
                        transform: `translateX(${dragOffset}%)`,
                        transition: 'transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        width: '200%'
                      }}
                    >
                      <div className="flex-shrink-0 p-4" style={{width: '50%'}} data-club-view="upcoming">
                        <ClubScheduleView filter="upcoming" onGameSelect={handleGameSelect} />
                      </div>
                      <div className="flex-shrink-0 p-4" style={{width: '50%'}} data-club-view="finished">
                        <ClubScheduleView filter="finished" onGameSelect={handleGameSelect} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Scroll to Top Button - Only on mobile */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-4 right-4 w-12 h-12 bg-fortuna-600 hover:bg-fortuna-700 text-white rounded-full shadow-lg transition-all duration-300 flex items-center justify-center lg:hidden z-20"
          aria-label="Nach oben scrollen"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
          </svg>
        </button>
      )}

      {/* Notification Dialog */}
      <NotificationDialog
        isOpen={showNotificationDialog}
        onClose={() => setShowNotificationDialog(false)}
        teamId={notificationContext.teamId}
        teamName={notificationContext.teamName}
        isClubNotification={notificationContext.isClub}
      />
    </div>
  );
}

export default App;
